import {constructRow} from '@tanstack/react-table'

import {getVisibleAgentTaskSubRows} from '../../../components/table/agent-task-utils'
import type {MemexItemModel} from '../../../models/memex-item-model'
import type {SubIssuesByParentId} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {getRowId} from '../helpers/get-row-id'
import type {CoreTable, Row, RowModel} from '../helpers/types'

/**
 * Creates nested sub-rows for a parent row.
 *
 * @param parentRow The parent row for which to get nested sub-rows
 * @param subIssuesByParentId A mapping of parent issue IDs to their corresponding sub-issue items
 * @param table The TanStack Table instance
 * @param rowModel The TanStack Table row model
 * @param options.copilotTasksEnabled When true, creates real sub-rows for agent tasks alongside sub-issues
 * @param visitedParentIds Tracks visited parent content IDs during recursion to prevent circular hierarchies
 *
 * @returns An array of nested sub-rows for the parent row
 */
export const getSubIssueSubRows = (
  parentRow: Row<MemexItemModel>,
  subIssuesByParentId: SubIssuesByParentId,
  table: CoreTable<MemexItemModel>,
  rowModel: RowModel<MemexItemModel>,
  options?: {copilotTasksEnabled?: boolean},
  visitedParentIds: Set<string | number> = new Set(),
) => {
  const parentContentId = parentRow.original.content.id

  // The server prevents circular sub-issue relationships at creation time, but cached
  // or stale data may still contain one. Stop recursion rather than looping indefinitely.
  if (visitedParentIds.has(parentContentId)) return []
  visitedParentIds.add(parentContentId)

  // Get sub-issue items available from the query cache by the parent issue's ID.
  const data = subIssuesByParentId[parentContentId]
  const subRows: Array<Row<MemexItemModel>> = []

  if (options?.copilotTasksEnabled) {
    const agentTasks = getVisibleAgentTaskSubRows(parentRow.original)
    for (const [index, task] of agentTasks.entries()) {
      const taskRowId = `${parentRow.id}-agent-task-${task.id ?? index}`
      const taskRow = constructRow(
        table,
        taskRowId,
        parentRow.original,
        subRows.length + index,
        parentRow.depth + 1,
        undefined,
        parentRow.id,
      )
      taskRow.agentAssignmentTaskData = {task}
      rowModel.flatRows.push(taskRow)
      rowModel.rowsById[taskRow.id] = taskRow
      subRows.push(taskRow)
    }
  }

  if (data) {
    // Initialize the custom sub-issue row metadata when grouping has not already done so.
    if (!parentRow.subIssueData) {
      parentRow.subIssueData = {subIssueDepth: 0}
    }

    const {subIssues: items, missingSubIssues} = data
    // Sub-issue depth is separate from row depth because row depth also includes grouping.
    const subIssueDepth = (parentRow.subIssueData?.subIssueDepth ?? -1) + 1
    for (const [index, item] of items.entries()) {
      // Build a row ID unique within its ancestors: `${parentRowId}-${itemId}`.
      const newRowId = getRowId(item, index, {id: parentRow.id})
      const newRow = constructRow(table, newRowId, item, index, parentRow.depth + 1, undefined, parentRow.id)
      newRow.subIssueData = {subIssueDepth}

      rowModel.flatRows.push(newRow)
      // Recursively construct this row's descendants.
      newRow.subRows = getSubIssueSubRows(newRow, subIssuesByParentId, table, rowModel, options, visitedParentIds)
      rowModel.rowsById[newRow.id] = newRow
      subRows.push(newRow)
    }

    // If the parent issue has missing sub-issues, add a custom row to represent them.
    // Sub-issues are considered missing if they are not in the project.
    if (missingSubIssues && missingSubIssues.length > 0) {
      const missingRow = constructRow(
        table,
        getRowId({id: 'missing'}, subRows.length, {id: parentRow.id}),
        parentRow.original,
        subRows.length,
        parentRow.depth + 1,
        undefined,
        parentRow.id,
      )
      missingRow.subIssueData = {subIssueDepth, missingSubIssues}
      rowModel.flatRows.push(missingRow)
      rowModel.rowsById[missingRow.id] = missingRow
      subRows.push(missingRow)
    }
  }

  return subRows
}
